import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { PROJECT_DASHBOARD_SVG } from 'src/app/common/svgs';
import { ITag, ITagType } from 'src/app/contract-management/models/settings.model';
import { ProjectTagService } from 'src/app/contract-management/services/project-tag.service';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox';
import { DatepickerComponent } from '../../../@core/components/ui/datepicker';
import { MultiSelectComponent } from '../../../@core/components/ui/multi-select';
import { PROJECT_KNOWLEDGE_BASE, TAG_DATA_TYPES, USER_TYPE } from '../../../@core/models/enums';
import { ICustomer, IMilestone, IProject, IProjectTag, IShareUser, IUser, MILESTONE_TYPE, SHARE_ACCESS, SHARE_FEATURE } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { MilestonesComponent } from '../../../contract-management/components/milestones/milestones.component';
import { MileStoneService } from '../../../contract-management/services/milestone.service';
import { ShareContractService } from '../../../contract-management/services/share-contract.service';
import { ShareService } from '../../../contract-management/services/share.service';
import { UserAvatarPipe } from "../../pipes/user-avatar-pipe";
import { ProjectKnowledgeDialogComponent, ProjectKnowledgeDialogResult } from '../project-knowledge-dialog/project-knowledge-dialog.component';
import { ShareToUsersComponent } from '../share-to-users/share-to-users.component';
import { StepperStepDirective } from '../stepper-layout/stepper-step.directive';
import { StepperService } from '../stepper-layout/stepper.service';
import { StepperConfig, StepperStepConfig } from '../stepper-layout/stepper.types';
import { StepperComponent } from '../stepper/stepper.component';

type ProjectTagItem = ITag | ITagType;

@Component({
    selector: 'app-project-form-dialog',
    templateUrl: './project-form-dialog.component.html',
    styleUrls: ['./project-form-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SelectModule,
        InputTextModule,
        DatepickerComponent,
        CheckboxComponent,
        MilestonesComponent,
        ShareToUsersComponent,
        AvatarModule,
        UserAvatarPipe,
        MultiSelectComponent,
        StepperComponent,
        StepperStepDirective,
        ProjectKnowledgeDialogComponent,
        TooltipModule,
        ButtonComponent,
        IconComponent
    ],
    providers: [StepperService]
})
export class ProjectFormDialogComponent implements OnInit, OnChanges, AfterViewInit, OnChanges {
    private _visible: boolean = false;
    @Input() set visible(value: boolean) {
        if (value === true && this._visible === false && this.mode === 'edit' && this.projectId) {
            this.loadMilestones();
        }
        this._visible = value;
        this.visibleChange.emit(value);
    }
    get visible(): boolean {
        return this._visible;
    }

    @Input() mode: 'create' | 'edit' = 'create';
    @Input() projectFormData: Partial<IProject> = {
        managers: []
    };
    @Input() managerOptions: any[] = [];
    @Input() projectId: string = '';
    @Input() submitLoading: boolean = false;
    @Input() permissions: {
        canView: boolean,
        canEdit: boolean,
        canComment: boolean,
        restricted: boolean
    } = {
            canView: true,
            canEdit: true,
            canComment: true,
            restricted: false
        };

    // Stepper configuration
    stepperConfig = signal<StepperConfig | null>(null);

    @Output() submit = new EventEmitter<{
        formData: Partial<IProject>,
        projectKnowledge: {
            files: File[],
            knowledgeBases: PROJECT_KNOWLEDGE_BASE[]
        },
        milestones: IMilestone[],
        deletedMilestones: string[],
        teamMembers: IShareUser[]
        workspaceShare: boolean,
        projectShare: boolean,
        needAccessUsers?: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[]
    }>();
    @Output() cancel = new EventEmitter<void>();
    @Output() hide = new EventEmitter<void>();
    @Output() visibleChange = new EventEmitter<boolean>();

    @ViewChild(MilestonesComponent) milestonesComp?: MilestonesComponent;

    minimumDate: Date = new Date(new Date().setHours(0, 0, 0, 0));

    createProjectMilestones: IMilestone[] = [];
    deletedMilestoneIds: string[] = [];

    projectTagsTypes: ITagType[] = [];
    projectTags: { _id?: string; tagType: ITagType; value: string | string[]; isNew?: boolean; }[] = [];
    projectTagValues: { tagTypeId: string, name: string, tagId?: string }[] = [];
    searchProjectTagType: string = '';
    tagOptions: { label: string, value: string, tagTypeId: string, tagId: string }[] = [];
    tagOptionsMap: Map<string, { label: string, value: string, tagTypeId: string }[]> = new Map();
    showEmptyDropdownWarning: boolean = false;
    emptyDropdownWarningText: string = 'This tagtype doesn\'t have any options , click here to redirect to tag type settings to add options';
    selectedTagOptionsForTagTypeId: Map<string, string[]> = new Map();


    TAG_DATA_TYPES = TAG_DATA_TYPES;

    // Type-safe getter/setter for managers multi-select
    get projectManagers(): any[] {
        return this.projectFormData.managers || [];
    }

    set projectManagers(value: any[]) {
        this.projectFormData.managers = value;
    }

    // Type-safe getter/setter for due date (handle Date | undefined to Date | null)
    get projectDueDate(): Date | null {
        return this.projectFormData.dueDate ?? null;
    }

    set projectDueDate(value: Date | null) {
        this.projectFormData.dueDate = value ?? undefined;
    }
    @Input() shareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare: boolean; projectShare: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
    currentUser: IUser | null | undefined = null;
    needAccessUsers: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[] = [];

    currentUserRole: SHARE_ACCESS | SHARE_ACCESS[] | null = null;

    // Project Knowledge properties
    showProjectKnowledgeDialog: boolean = false;
    projectKnowledgeFiles: File[] = [];
    projectKnowledgeBases: PROJECT_KNOWLEDGE_BASE[] = [];
    projectKnowledgeUploadMode: 'withKB' | 'withoutKB' = 'withKB';
    PROJECT_KNOWLEDGE_BASE = PROJECT_KNOWLEDGE_BASE;

    // Helper method to check if user has specific role (handles both single value and array)
    private hasRole(role: SHARE_ACCESS | SHARE_ACCESS[] | undefined, checkRole: SHARE_ACCESS): boolean {
        if (!role) return false;
        if (Array.isArray(role)) {
            return role.includes(checkRole);
        }
        return role === checkRole;
    }

    constructor(
        private toast: ToastService,
        private milestoneService: MileStoneService,
        private authService: AuthenticationService,
        private shareUser: ShareContractService,
        private shareService: ShareService,
        private projectTagService: ProjectTagService = inject(ProjectTagService),
        public stepperService: StepperService = inject(StepperService)
    ) {
        this.authService.currentUser.subscribe(res => {
            this.currentUser = res;
        });
    }

    ngOnInit(): void {
        this.initializeComponent();
        this.getProjectTagsTypes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const modeChanged = changes['mode'] && !changes['mode'].firstChange;
        if (!modeChanged) return;
        if (this.mode === 'edit' && this.projectId) {
            this.loadMilestones();
        }
        this.initializeStepperConfig();
        if (changes['projectFormData'] && !changes['projectFormData'].firstChange) {
            this.initializeFormData();
        }
    }

    ngAfterViewInit(): void {
        if (this.stepperService.currentStepIndex() === 1) {
            this.syncMilestonesData();
        }
    }

    private initializeComponent(): void {
        this.initializeStepperConfig();
        this.resetState();
        this.initializeFormData();
        this.loadMilestones();
        this.getCurrentUserRole();
    }

    private initializeStepperConfig(): void {
        const steps: StepperStepConfig[] = [
            {
                id: 'create-project',
                label: this.mode === 'edit' ? 'Edit project' : 'Create project',
                title: this.mode === 'edit' ? 'Edit project' : 'Create project',
                subtitle: "Start with a custom one to perfectly match your project's specific needs",
                svg: PROJECT_DASHBOARD_SVG.CREATE_PROJECT
            },
            {
                id: 'add-milestones',
                label: this.mode === 'edit' ? 'Edit milestones' : 'Add milestones',
                title: this.mode === 'edit' ? 'Edit milestones' : 'Add milestone',
                subtitle: "Start with a custom one to perfectly match your project's specific needs",
                svg: PROJECT_DASHBOARD_SVG.ADD_MILESTONE
            },
            {
                id: 'add-team',
                label: this.mode === 'edit' ? 'Edit team' : 'Add team',
                title: this.mode === 'edit' ? 'Edit team' : 'Add team',
                subtitle: "Start with a custom one to perfectly match your project's specific needs",
                svg: PROJECT_DASHBOARD_SVG.ADD_TEAM
            }
        ];

        this.stepperConfig.set({
            subtitle: "A completely new way to handle your construction documents",
            steps: steps,
            currentStepIndex: 0,
            dialogWidth: '80vw',
            dialogHeight: '90vh'
        });
    }

    private initializeFormData(): void {
        this.projectFormData = {
            projectId: '',
            name: '',
            managers: [],
            customer: '',
            internal: false,
            ...this.projectFormData
        };

        // Ensure managers are part of team in edit mode
        if (this.mode === 'edit' && this.projectFormData.managers?.length) {
            // set map for tag 
            if (this.projectFormData.tag?.length) {
                for (const tag of this.projectFormData.tag) {
                    this.selectedTagOptionsForTagTypeId.set(tag.tagTypeId as string, tag.value as string[]);
                }
            }
            this.ensureManagersInTeam();
            this.getProjectTags();

        }
    }

    private ensureManagersInTeam(): void {
        const managerIds = this.projectFormData?.managers || [];
        if (!managerIds.length) return;

        for (const managerId of managerIds) {
            const managerInTeam = this.shareDetails.users.find((user: IShareUser) => {
                if (typeof user.user === 'string') {
                    return user.user === managerId;
                } else if (user.user && typeof user.user === 'object') {
                    return (user.user as IUser)._id === managerId;
                }
                return false;
            });

            if (!managerInTeam) {
                const managerOption = this.managerOptions.find((option: any) => option.value === managerId);
                if (managerOption) {
                    this.shareDetails.users.push({
                        name: managerOption.label,
                        user: managerId,
                        email: managerOption.email,
                        type: USER_TYPE.INTERNAL,
                        role: SHARE_ACCESS.ADMIN,
                    } as IShareUser);
                }
            }
        }
    }

    private getMilestonesData(): void {
        if (this.milestonesComp) {
            this.createProjectMilestones = this.milestonesComp.editableMilestones() as IMilestone[];
            this.deletedMilestoneIds = this.milestonesComp.deletedMilestoneIds();
        }
    }

    private loadMilestones(): void {
        if (this.mode === 'edit' && this.projectId) {
            this.milestoneService.getMilestones(MILESTONE_TYPE.PROJECT, this.projectId).subscribe(res => {
                if (res.success) {
                    this.createProjectMilestones = res.data as IMilestone[];
                    if (this.milestonesComp) {
                        this.milestonesComp.editableMilestones.set(this.createProjectMilestones.map(m => ({
                            ...m,
                            date: m.date,
                            reminder: m.reminder
                        })));
                        this.milestonesComp.deletedMilestoneIds.set([]);
                    }
                }
            });
        } else {
            this.milestoneService.getMilestones(MILESTONE_TYPE.PROJECT).subscribe(res => {
                if (res.success) {
                    this.createProjectMilestones = (res.data as IMilestone[]).map(m => {
                        const clone = { ...m } as any;
                        delete clone._id;
                        return {
                            ...clone,
                            date: new Date()
                        } as IMilestone;
                    });
                }
            });
        }
    }

    isFormValid(): boolean {
        const { projectId, name, customer, internal } = this.projectFormData as Partial<IProject> & { customer: ICustomer | string | null } & { tags: ITag[] };
        const basicFieldsValid = !!(projectId && name && (internal ? true : customer));
        const mandatoryTagsValid = this.areAllMandatoryTagsFilled();

        return basicFieldsValid && mandatoryTagsValid;
    }

    areAllMandatoryTagsFilled(): boolean {
        if (this.mode === 'create') {
            const mandatoryTagTypes = this.projectTagsTypes.filter(tagType => tagType.mandatory);
            for (const tagType of mandatoryTagTypes) {
                const tagValue = this.selectedTagOptionsForTagTypeId.get(tagType._id || '');
                if (!tagValue) {
                    return false;
                }
                if (Array.isArray(tagValue)) {
                    if (tagValue.length === 0) {
                        return false;
                    }
                }
                else if (typeof tagValue === 'string' && (tagValue as string).trim() === '') {
                    return false;
                }
            }
        } else if (this.mode === 'edit') {
            const mandatoryTags = this.projectTags.filter(tag => tag.tagType.mandatory);
            for (const tag of mandatoryTags) {

                const updatedValue = this.selectedTagOptionsForTagTypeId.get(tag.tagType._id || '');
                const hasBeenModified = this.selectedTagOptionsForTagTypeId.has(tag.tagType._id || '');

                const tagValue = hasBeenModified ? updatedValue : tag.value;

                if (!tagValue) {
                    return false;
                }
                if (Array.isArray(tagValue)) {
                    if (tagValue.length === 0) {
                        return false;
                    }
                } else if (typeof tagValue === 'string' && (tagValue as string).trim() === '') {
                    return false;
                }
            }
        }

        return true;
    }

    onSubmit(): void {
        if (!this.isFormValid()) {
            return;
        }

        this.getMilestonesData();

        let tagsToSend = []
        if (this.mode === 'create') {
            for (const tagtype of this.projectTagsTypes) {
                let tagValue: string | string[] | undefined = this.selectedTagOptionsForTagTypeId.get(tagtype._id || '');
                if (!tagValue) {
                    if (tagtype.dataType === TAG_DATA_TYPES.DROPDOWN) {
                        tagValue = []
                    } else {
                        tagValue = '';
                    }
                }
                tagsToSend.push({
                    tagTypeId: tagtype._id || '',
                    value: tagValue as string | string[],
                });
            }
        } else {
            tagsToSend = this.projectTags;
        }
        this.submit.emit({
            formData: {
                ...this.projectFormData,
                tags: tagsToSend,
            } as any,
            projectKnowledge: {
                files: this.projectKnowledgeFiles ? [...this.projectKnowledgeFiles] : [],
                knowledgeBases: this.projectKnowledgeBases ? [...this.projectKnowledgeBases] : []
            },
            milestones: this.createProjectMilestones,
            deletedMilestones: this.deletedMilestoneIds,
            teamMembers: this.shareDetails.users,
            workspaceShare: this.shareDetails.workspaceShare,
            projectShare: this.shareDetails.projectShare
        });
    }

    onCancel(): void {
        this.resetState(false);
        this.cancel.emit();
        this.visible = false;
    }

    onHide(): void {
        this.resetState(false);
        this.hide.emit();
        this.visible = false;
    }

    onStepperClose(): void {
        this.onHide();
    }

    onStepChange(event: any): void {
        this.syncMilestonesOnStepChange();
    }

    onNextStepClick(): void {
        const currentIndex = this.stepperService.currentStepIndex();
        if (currentIndex === 0) {
            if (!this.isFormValid()) {
                this.toast.error('Please fill in all required fields.');
                return;
            }
            this.addManagerToShareDetails();
        }
        if (currentIndex === 1) {
            this.getMilestonesData();
        }
    }

    onPreviousStepClick(): void {
        this.syncMilestonesOnStepChange();
    }

    private syncMilestonesData(): void {
        if (this.milestonesComp) {
            this.milestonesComp.editableMilestones.set([...this.createProjectMilestones]);
            if (this.deletedMilestoneIds.length > 0) {
                this.milestonesComp.deletedMilestoneIds.set([...this.deletedMilestoneIds]);
            }
        }
    }

    private syncMilestonesOnStepChange(): void {
        if (this.stepperService.currentStepIndex() === 1) {
            this.syncMilestonesData();
        }
    }

    onMilestonesChanged(): void {
        this.getMilestonesData();
    }
    private addManagerToShareDetails(): void {
        const managerIds = this.projectFormData?.managers || [];
        if (!managerIds.length) return;

        // Remove existing managers - handle both array and single value roles
        this.shareDetails.users = this.shareDetails.users.filter((user: IShareUser) => !this.hasRole(user.role, SHARE_ACCESS.ADMIN));

        // Add new managers
        for (const managerId of managerIds) {
            const managerOption = this.managerOptions.find((option: any) => option.value === managerId);
            if (!managerOption) continue;

            const existingUserIndex = this.shareDetails.users.findIndex((user: IShareUser) => {
                if (typeof user.user === 'string') {
                    return user.user === managerId;
                } else if (user.user && typeof user.user === 'object') {
                    return (user.user as IUser)._id === managerId;
                }
                return false;
            });

            if (existingUserIndex !== -1) {
                this.shareDetails.users[existingUserIndex].user = managerOption.user;
                this.shareDetails.users[existingUserIndex].role = SHARE_ACCESS.ADMIN;
                this.shareDetails.users[existingUserIndex].email = managerOption.email;
                this.shareDetails.users[existingUserIndex].name = managerOption.label;
                continue;
            }

            this.shareDetails.users.push({
                name: managerOption.label,
                user: managerOption.user,
                email: managerOption.email,
                type: USER_TYPE.INTERNAL,
                role: SHARE_ACCESS.ADMIN,
            } as IShareUser);
        }
    }

    giveAccessToUsers() {
        for (const user of this.needAccessUsers) {
            this.shareService.giveAccessToShareUser({
                name: user.user?.fullName ?? (user.user?.firstName + ' ' + user.user?.lastName),
                user: user.user._id,
                email: user.user.email ?? '',
                type: USER_TYPE.INTERNAL as any,
                role: user.shareUser.role,
            }, this.projectId, SHARE_FEATURE.PROJECT).subscribe(res => {
                if (res.data.hasAccess) {
                    this.toast.success('User ' + user.user.fullName + ' Added to Workspace successfully');
                }
            })
        }
    }

    onFinish(): void {
        if (!this.isFormValid()) {
            const { managers } = this.projectFormData
            if (!managers?.length) {
                this.toast.error('Please add at least one admin to the project.');
                return;
            }
            return;
        }

        this.getMilestonesData();
        if (this.needAccessUsers.length > 0 && this.mode !== 'create') {
            this.giveAccessToUsers();
        }

        let tagsToSend = []
        for (const tagtype of this.projectTagsTypes) {
            let tagValue: string | string[] | undefined = this.selectedTagOptionsForTagTypeId.get(tagtype._id || '');
            if (!tagValue) {
                if (tagtype.dataType === TAG_DATA_TYPES.DROPDOWN) {
                    tagValue = []
                } else {
                    tagValue = '';
                }
            }
            tagsToSend.push({
                tagTypeId: tagtype._id || '',
                value: tagValue as string | string[],
            });
        }

        this.submit.emit({
            formData: {
                ...this.projectFormData,
                tag: tagsToSend,
            } as any,
            projectKnowledge: {
                files: this.projectKnowledgeFiles ? [...this.projectKnowledgeFiles] : [],
                knowledgeBases: this.projectKnowledgeBases ? [...this.projectKnowledgeBases] : []
            },
            milestones: this.createProjectMilestones,
            deletedMilestones: this.deletedMilestoneIds,
            teamMembers: this.shareDetails.users,
            workspaceShare: this.shareDetails.workspaceShare,
            projectShare: this.shareDetails.projectShare,
            needAccessUsers: this.needAccessUsers
        });
    }

    getCurrentUserRole(): void {
        if (!this.currentUser) return;

        const currentUserInShare = this.shareDetails.users.find((user: IShareUser) => {
            if (typeof user.user === 'string') {
                return user.user === this.currentUser?._id;
            } else if (user.user && typeof user.user === 'object') {
                return (user.user as IUser)._id === this.currentUser?._id;
            }
            return false;
        });

        this.currentUserRole = currentUserInShare?.role || null;
    }

    private resetState(resetShareDetails: boolean = true) {
        this.stepperService.setCurrentStep(0);
        this.deletedMilestoneIds = [];
        if (this.milestonesComp) {
            this.milestonesComp.deletedMilestoneIds.set([]);
        }
        this.needAccessUsers = [];
        this.projectTagValues = [];
        this.projectTags = [];
        this.selectedTagOptionsForTagTypeId.clear();
        if (resetShareDetails) {
            this.createProjectMilestones = [];

            if (this.mode === 'create' || this.shareDetails.users.length === 0) {
                this.shareDetails = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
                if (this.currentUser) {
                    this.shareDetails.users.push({
                        name: this.currentUser.fullName as string,
                        user: this.currentUser as IUser,
                        email: (this.currentUser as IUser).email as string,
                        type: USER_TYPE.INTERNAL,
                        role: SHARE_ACCESS.OWNER,
                    } as IShareUser);
                }
            } else if (this.mode === 'edit' && this.currentUser) {
                const hasOwner = this.shareDetails.users.some(user => this.hasRole(user.role, SHARE_ACCESS.OWNER));
                // if (!hasOwner) {
                //     this.shareDetails.users.push({
                //         name: this.currentUser.fullName as string,
                //         user: this.currentUser as IUser,
                //         email: (this.currentUser as IUser).email as string,
                //         type: USER_TYPE.INTERNAL,
                //         role: SHARE_ACCESS.OWNER,
                //     } as IShareUser);
                // }
            }
        } else {
            if (this.mode === 'create') {
                this.loadMilestones();
            }
        }
    }

    changeSharedWithWorkspace(value: boolean): void {
        this.shareDetails.workspaceShare = value;
    }

    changeSharedWithProject(value: boolean): void {
        this.shareDetails.projectShare = value;
    }



    onAddMember(shareUser: Partial<IShareUser>): void {
        if (shareUser.role === SHARE_ACCESS.ADMIN) {
            this.addUserToManagers(shareUser);
        }
    }

    onUpdateMember(shareUser: IShareUser): void {
        const userId = this.getUserIdFromShareUser(shareUser);
        if (!userId) return;

        if (shareUser.role === SHARE_ACCESS.ADMIN) {
            this.addUserToManagers(shareUser);
        } else {
            this.removeUserFromManagers(userId);
        }
    }

    onRemoveMember(shareUser: Partial<IShareUser>): void {
        const userId = this.getUserIdFromShareUser(shareUser);
        if (!userId) return;

        if ((shareUser.role as SHARE_ACCESS[]).includes(SHARE_ACCESS.ADMIN)) {
            this.removeUserFromManagers(userId);
        }
        this.shareDetails.users = this.shareDetails.users.filter((user: IShareUser) => (user?.user as IUser)?._id != userId);
    }

    private getUserIdFromShareUser(shareUser: Partial<IShareUser>): string | null {
        if (typeof shareUser.user === 'string') {
            return shareUser.user;
        } else if (shareUser.user && typeof shareUser.user === 'object') {
            return (shareUser.user as IUser)._id;
        }
        return null;
    }

    private addUserToManagers(shareUser: Partial<IShareUser>): void {
        const userId = this.getUserIdFromShareUser(shareUser);
        if (!userId) return;

        const currentManagers = this.projectFormData.managers || [];

        if (!currentManagers.includes(userId)) {
            this.projectFormData.managers = [...currentManagers, userId];
        }
    }

    private removeUserFromManagers(userId: string): void {
        const currentManagers = this.projectFormData.managers || [];
        this.projectFormData.managers = currentManagers.filter(managerId => managerId !== userId);
    }

    getProjectTags() {
        this.projectTagService.getProjectTags(this.projectId).subscribe((res: any) => {
            if (res.success) {
                const { tags, newTagTypes } = res.data;
                const existing = tags.map((tag: IProjectTag) => {
                    const tagType = tag.tagTypeId as ITagType;
                    return {
                        _id: tag._id,
                        tagType,
                        value:
                            tagType.dataType === this.TAG_DATA_TYPES.DATE && typeof tag.value === 'string'
                                ? new Date(tag.value)
                                : tag.value,
                        isNew: false
                    };
                });
                const fresh = newTagTypes.map((type: ITagType) => ({
                    tagType: type,
                    value: '',
                    isNew: true
                }));
                this.projectTags = [...existing, ...fresh];
                this.projectTagValues = tags.map((tag: IProjectTag) => {
                    const tagType = tag.tagTypeId as ITagType;
                    if (tagType.dataType === this.TAG_DATA_TYPES.DROPDOWN) {
                        return {
                            tagTypeId: tagType._id || '',
                            name: tag.value as string,
                            tagId: tag.value as string
                        };
                    }
                    return {
                        tagTypeId: tagType._id || '',
                        name: tag.value as string
                    };
                });

            }
        });
    }



    trackTag(index: number, tag: ProjectTagItem) {
        if (this.isITag(tag)) return tag._id;
        return tag._id || tag;
    }

    isTagTypeObject(tagType: string | ITagType): tagType is ITagType {
        return tagType !== null && typeof tagType === 'object';
    }

    getProjectTagsTypes() {
        this.projectTagService.getProjectTagType(this.searchProjectTagType).subscribe((res: any) => {
            if (res.success) {
                this.projectTagsTypes = res.data as ITagType[];
                this.getOptionsForTagType(this.projectTagsTypes.map(tagType => tagType._id || ''));
            }
        });
    }


    getOptionsForTagType(tagTypeIds: string[]) {
        this.projectTagService.getTagOptions(tagTypeIds).subscribe((res) => {
            if (res.success) {
                for (const option of res.data) {
                    if (this.tagOptionsMap.has(option.tagTypeId)) {
                        this.tagOptionsMap.get(option.tagTypeId)?.push({
                            label: option.name,
                            value: option.tagId,
                            tagTypeId: option.tagTypeId,
                        });
                    } else {
                        this.tagOptionsMap.set(option.tagTypeId, [{ label: option.name, value: option.tagId, tagTypeId: option.tagTypeId }]);
                    }
                }
            }
        });
    }

    getOptionsForTag(tagTypeId: string) {
        return this.tagOptionsMap.get(tagTypeId) || [];
    }

    getTagValue(tagTypeId: string) {
        // read from selectedTagOptionsForTagTypeId if it exists
        const selectedValue = this.selectedTagOptionsForTagTypeId.get(tagTypeId);
        if (selectedValue !== undefined) {
            return selectedValue;
        }
        return this.projectTagValues.find(o => o.tagTypeId === tagTypeId)?.name || '';
    }

    setTagValue(tagTypeId: string, value: any): void {
        this.selectedTagOptionsForTagTypeId.set(tagTypeId, value);
    }

    isITag(item: ProjectTagItem): item is ITag {
        return typeof item === 'object' && '_id' in item;
    }

    isITagType(item: ProjectTagItem): item is ITagType {
        return typeof item === 'object' && !('_id' in item);
    }

    getEditModeValue(tag: any): string {
        const dataType = tag.tagType.dataType;
        if ((dataType === this.TAG_DATA_TYPES.DROPDOWN) && tag.value) {
            const option = this.tagOptions.find(o => o.tagId === tag.value);
            return option?.label || tag.value;
        }

        return tag.value;
    }

    hasNoOptions(tagTypeId: string): boolean {
        const options = this.tagOptionsMap.get(tagTypeId);
        return !options || options.length === 0;
    }

    hasAnyEmptyDropdown(): boolean {
        if (this.mode === 'create') {
            return this.projectTagsTypes.some(tag =>
                tag.dataType === this.TAG_DATA_TYPES.DROPDOWN &&
                this.hasNoOptions(tag._id || '')
            );
        }
        return this.projectTags.some(tag =>
            tag.tagType.dataType === this.TAG_DATA_TYPES.DROPDOWN &&
            this.hasNoOptions(tag.tagType._id || '')
        );
    }

    // Project Knowledge methods
    openProjectKnowledgeDialog(): void {
        this.showProjectKnowledgeDialog = true;
    }

    onProjectKnowledgeUpload(result: ProjectKnowledgeDialogResult): void {
        // Update state whenever dialog closes (whether uploaded or just closed)
        // Create new array references to ensure proper state management
        this.projectKnowledgeFiles = result.files ? [...result.files] : [];
        this.projectKnowledgeBases = result.knowledgeBases ? [...result.knowledgeBases] : [];
        // Determine upload mode based on whether knowledge bases were selected
        this.projectKnowledgeUploadMode = result.knowledgeBases && result.knowledgeBases.length > 0 ? 'withKB' : 'withoutKB';
        this.showProjectKnowledgeDialog = false;
    }

    getProjectKnowledgeFileCount(): number {
        return this.projectKnowledgeFiles.length;
    }

}
